import {
  Badge,
  Box,
  Button,
  Card,
  Drawer,
  Flex,
  Heading,
  HStack,
  Icon,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Building, Building2, Space, Target } from 'lucide-react'
import mqtt from 'mqtt'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import TreeView, {
  flattenTree,
  type INode,
  type INodeRendererProps,
} from 'react-accessible-treeview'
import { FaFileAlt, FaFolder, FaFolderOpen } from 'react-icons/fa'
import { IoMdArrowDropdown, IoMdArrowDropright } from 'react-icons/io'

import UNSLoader from '~/components/UNSLoader'

interface TopicNode {
  name: string
  id: number
  children?: TopicNode[]
  parent: number | null
  metadata?: string
}

export interface UnsExplorerProps {
  wsAddress: string
  /**
   * Complete broker URL, taking precedence over [wsAddress].
   *
   * [wsAddress] hardcodes `wss://` and port 443, so it cannot reach a local
   * broker. Pass this (via `resolveCentralBroker()`) to keep a page usable in
   * local development instead of connecting to the production brokers.
   */
  wsUrl?: string
  username: string
  password: string
  rootName: string
  topicFilters: string | string[]
  clientIdPrefix: string
  pageTitle?: string
  pageSubtitle?: string
  treeAriaLabel?: string
  withLevelIcons?: boolean
  intro?: ReactNode
}

let nodeIdCounter = 1
function getNextNodeId(): number {
  return nodeIdCounter++
}

const LEVEL_ICONS = [Building2, Building, Target, Space] as const
const LEVEL_COLORS = ['purple.500', 'blue.500', 'green.500', 'orange.500'] as const

export function UnsExplorer({
  wsAddress,
  wsUrl,
  username,
  password,
  rootName,
  topicFilters,
  clientIdPrefix,
  pageTitle = 'UNS Explorer',
  pageSubtitle = 'Dive into the UNS structure',
  treeAriaLabel = 'UNS Explorer Tree',
  withLevelIcons = false,
  intro,
}: UnsExplorerProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [topicsRoot, setTopicsRoot] = useState<TopicNode>({
    name: rootName,
    id: 0,
    children: [],
    parent: null,
  })
  const [isOpen, setIsOpen] = useState(false)
  const [elementId, setElementId] = useState<number | undefined>()
  const [elementName, setElementName] = useState<string | undefined>()
  const [elementMetadata, setElementMetadata] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [messagesPerSecond, setMessagesPerSecond] = useState(0)

  const pendingUpdatesRef = useRef<Map<string, string>>(new Map())
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const topicsRootRef = useRef<TopicNode>(topicsRoot)
  const clientRef = useRef<mqtt.MqttClient | null>(null)
  const messageCountRef = useRef(0)
  const lastCountRef = useRef(0)
  const mpsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    topicsRootRef.current = topicsRoot
  }, [topicsRoot])

  const treeData = useMemo(() => {
    try {
      if (!topicsRoot.children || topicsRoot.children.length === 0) {
        return []
      }
      return flattenTree(topicsRoot as Parameters<typeof flattenTree>[0])
    } catch {
      return []
    }
  }, [topicsRoot])

  const processBatchedUpdates = useCallback(() => {
    const updates = pendingUpdatesRef.current
    if (updates.size === 0) {
      return
    }

    const nextData: TopicNode = JSON.parse(JSON.stringify(topicsRootRef.current))

    updates.forEach((message, topic) => {
      const segments = topic.split('/')
      let currentNode: TopicNode = nextData

      segments.forEach((segment: string) => {
        if (!currentNode.children) {
          currentNode.children = []
        }
        let existingNode = currentNode.children.find((n) => n.name === segment)
        if (!existingNode) {
          const newNode: TopicNode = {
            id: getNextNodeId(),
            name: segment,
            metadata: message,
            parent: currentNode.id,
          }
          currentNode.children.push(newNode)
          existingNode = newNode
        } else {
          existingNode.metadata = message
        }
        currentNode = existingNode
      })
    })

    pendingUpdatesRef.current = new Map()
    setTopicsRoot(nextData)
  }, [])

  const queueUpdate = useCallback(
    (topic: string, message: string) => {
      pendingUpdatesRef.current.set(topic, message)

      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
      updateTimeoutRef.current = setTimeout(() => {
        processBatchedUpdates()
      }, 500)
    },
    [processBatchedUpdates],
  )

  const topicFiltersKey = Array.isArray(topicFilters) ? topicFilters.join('\n') : topicFilters

  // biome-ignore lint/correctness/useExhaustiveDependencies: topicFiltersKey is the memoized stable key; using raw topicFilters would reconnect MQTT every render
  useEffect(() => {
    const brokerUrl = wsUrl || (wsAddress ? `wss://${wsAddress}:443/mqtt` : '')
    if (!brokerUrl) {
      return
    }

    setIsLoading(true)
    nodeIdCounter = 1

    const options: mqtt.IClientOptions = {
      protocol: 'wss',
      port: 443,
      keepalive: 20,
      clientId: `${clientIdPrefix}_${Math.random().toString(16).substr(2, 8)}`,
      username,
      password,
    }

    const client = mqtt.connect(brokerUrl, options)
    clientRef.current = client

    client.on('message', (topic, message) => {
      messageCountRef.current++
      queueUpdate(topic.toString(), message.toString())
    })

    client.on('connect', () => {
      console.log(`Connected to ${brokerUrl}`)
      setIsConnected(true)
      const topics = Array.isArray(topicFilters) ? topicFilters : [topicFilters]
      client.subscribe(topics, { qos: 1 }, (err) => {
        if (err) {
          console.log(err)
        }
        setTimeout(() => {
          processBatchedUpdates()
          setIsLoading(false)
        }, 1000)
      })
    })

    client.on('close', () => {
      setIsConnected(false)
    })

    client.on('error', (err) => {
      console.log('Error happened', err)
    })

    mpsIntervalRef.current = setInterval(() => {
      const current = messageCountRef.current
      const mps = current - lastCountRef.current
      lastCountRef.current = current
      setMessagesPerSecond(mps)
    }, 1000)

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
      if (mpsIntervalRef.current) {
        clearInterval(mpsIntervalRef.current)
      }
      client.end()
      console.log('Disconnected')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    wsAddress,
    wsUrl,
    username,
    password,
    topicFiltersKey,
    clientIdPrefix,
    queueUpdate,
    processBatchedUpdates,
  ])

  const showValue = useCallback((element: INode) => {
    setElementId(element.id as number)
    setElementName(element.name)

    const metadata = element.metadata
    if (metadata === null || metadata === undefined) {
      setElementMetadata(null)
    } else if (typeof metadata === 'string') {
      setElementMetadata(metadata)
    } else if (typeof metadata === 'object') {
      let targetMetadata: string | null = null
      for (const property in metadata as Record<string, unknown>) {
        const value = (metadata as Record<string, unknown>)[property]
        if (targetMetadata) {
          targetMetadata += `${value}`
        } else {
          targetMetadata = `${value}`
        }
      }
      setElementMetadata(targetMetadata)
    } else {
      setElementMetadata(String(metadata))
    }
    setIsOpen(true)
  }, [])

  const closeNodeView = useCallback(() => {
    setIsOpen(false)
  }, [])

  const nodeRenderer = useCallback(
    (props: INodeRendererProps) => {
      const { element, isBranch, isExpanded, isDisabled, getNodeProps, level, handleExpand } = props
      const hasChildren = element.children && element.children.length > 0

      let iconAs: typeof FaFolder | typeof Building2
      let iconColor: string
      if (withLevelIcons && level >= 1 && level <= 4) {
        iconAs = LEVEL_ICONS[level - 1]
        iconColor = LEVEL_COLORS[level - 1]
      } else if (isBranch || hasChildren) {
        iconAs = isExpanded ? FaFolderOpen : FaFolder
        iconColor = 'yellow.500'
      } else {
        iconAs = FaFileAlt
        iconColor = 'blue.400'
      }

      return (
        <Box
          {...getNodeProps({ onClick: handleExpand })}
          pl={`${(level - 1) * 20 + 8}px`}
          py={1}
          opacity={isDisabled ? 0.5 : 1}
          cursor="pointer"
          _hover={{ bg: 'bg.subtle' }}
          borderRadius="md"
          display="flex"
          alignItems="center"
        >
          <HStack gap={2} flex={1}>
            {isBranch || hasChildren ? (
              <Icon
                as={isExpanded ? IoMdArrowDropdown : IoMdArrowDropright}
                boxSize={5}
                color="fg.muted"
                onClick={(e) => {
                  e.stopPropagation()
                  handleExpand(e)
                }}
              />
            ) : (
              <Box w={5} />
            )}

            <Icon as={iconAs} boxSize={4} color={iconColor} />

            {isBranch || hasChildren ? (
              <Text fontSize="md" fontWeight="medium" color="content.primary">
                {element.name}
              </Text>
            ) : (
              <Text
                fontSize="md"
                color="blue.600"
                _hover={{ textDecoration: 'underline' }}
                onClick={(e) => {
                  e.stopPropagation()
                  showValue(element)
                }}
              >
                {element.name}
              </Text>
            )}
          </HStack>
        </Box>
      )
    },
    [showValue, withLevelIcons],
  )

  return (
    <Flex direction="column" h="100%" gap={4}>
      <Box>
        <Heading size="2xl" mb="2">
          {pageTitle}
        </Heading>
        <Text mb="3" fontSize="md" color="fg.muted">
          {pageSubtitle}
        </Text>
        <HStack gap={4}>
          <Badge colorPalette={isConnected ? 'green' : 'red'} variant="subtle" px={2} py={1}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
          <Text fontSize="sm" color="fg.muted">
            {treeData.length} topics &middot; {messagesPerSecond} msg/s
          </Text>
        </HStack>
      </Box>

      {intro && (
        <Card.Root>
          <Card.Header mb="4">{intro}</Card.Header>
        </Card.Root>
      )}

      <Card.Root flex={1} minH={0} overflow="auto">
        <Card.Body>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" h="100%" minH="400px">
              <UNSLoader />
            </Box>
          ) : treeData.length > 0 ? (
            <Box className="uns-tree-container">
              <TreeView
                data={treeData}
                aria-label={treeAriaLabel}
                defaultExpandedIds={[0]}
                nodeRenderer={nodeRenderer}
              />
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" h="100%" minH="400px">
              <Text color="fg.muted">No data available. Waiting for MQTT messages...</Text>
            </Box>
          )}
        </Card.Body>
      </Card.Root>

      <Drawer.Root placement="bottom" open={isOpen} onOpenChange={(e) => setIsOpen(e.open)}>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.Header borderBottomWidth="1px">
                <Heading size="md">Node Information</Heading>
              </Drawer.Header>
              <Drawer.Body py={4}>
                <VStack align="start" gap={3}>
                  <HStack>
                    <Text fontWeight="semibold" color="content.secondary">
                      Node ID:
                    </Text>
                    <Text>{elementId}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold" color="content.secondary">
                      Node Name:
                    </Text>
                    <Text>{elementName}</Text>
                  </HStack>
                  <Box>
                    <Text fontWeight="semibold" color="content.secondary" mb={1}>
                      Payload:
                    </Text>
                    <Box
                      bg="bg.subtle"
                      p={3}
                      borderRadius="md"
                      fontFamily="mono"
                      fontSize="sm"
                      maxH="200px"
                      overflow="auto"
                    >
                      {elementMetadata || 'No data'}
                    </Box>
                  </Box>
                </VStack>
                <Button mt={4} onClick={closeNodeView} colorPalette="orange">
                  Close
                </Button>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </Flex>
  )
}

export default UnsExplorer
